import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../api.js';
import { buildEvidenceUrl } from '../domain/sourceRef';
import { useUserProfile } from '../hooks/useUserProfile';
import RoleProfileCards from './RoleProfileCards';

/**
 * EntityDossierModal — one logical entity's full cross-document profile.
 *
 * GraphRAG dedups entities within a document only, so "Officer Garcia" is N
 * disconnected rows across N reports. The backend cross-document union
 * (``GET /cases/{id}/graph/entity-dossier``) folds those fragments into one
 * entity and aggregates everything the graph knows about them — relationships,
 * claims they made, claims made about them, events they took part in, and the
 * documents they appear in. This modal is the "speed to facts" surface: click a
 * person, see their whole story in one place, every line citation-ready.
 *
 * Opened by the clickable key-entity names in the inventory (KeyEntitiesBadge).
 * Seeds by ``entityId`` (any per-document member) or by ``name`` + type.
 */

const TYPE_LABEL = {
  PERSON: 'Person', LAW_ENFORCEMENT_OFFICER: 'Officer', DEFENDANT: 'Defendant',
  WITNESS: 'Witness', VICTIM: 'Victim', ATTORNEY: 'Attorney', JUDGE: 'Judge',
  ORGANIZATION: 'Organization', LOCATION: 'Location', ADDRESS: 'Address',
  VEHICLE: 'Vehicle', EVIDENCE_ITEM: 'Evidence', SUBSTANCE: 'Substance',
  WEAPON: 'Weapon', PHONE_NUMBER: 'Phone', ACCOUNT: 'Account',
};

function typeLabel(t) {
  return TYPE_LABEL[t] || (t ? t.replace(/_/g, ' ').toLowerCase() : '');
}

function relVerb(rel) {
  const v = (rel.relationship_type || '').replace(/_/g, ' ').toLowerCase();
  return rel.direction === 'incoming' ? `was ${v} by` : v;
}

export default function EntityDossierModal({ caseId, entityId, name, entityType, onClose }) {
  const [phase, setPhase] = useState('loading'); // loading | ready | empty | error
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { profile } = useUserProfile();
  const graphExplorerOn = !!profile?.features?.graph_explorer;
  const aliveRef = useRef(true);
  useEffect(() => () => { aliveRef.current = false; }, []);

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setPhase('loading');
    try {
      const params = new URLSearchParams();
      if (entityId) params.set('entity_id', entityId);
      else if (name) { params.set('name', name); if (entityType) params.set('entity_type', entityType); }
      const res = await api.get(`/cases/${caseId}/graph/entity-dossier?${params.toString()}`);
      if (!aliveRef.current) return;
      setData(res);
      setPhase(res && res.entity ? 'ready' : 'empty');
    } catch (e) {
      if (!aliveRef.current) return;
      setError(e.status === 404 ? 'No graph profile for this entity yet.' : (e.message || 'Could not load profile.'));
      setPhase('error');
    }
  }, [caseId, entityId, name, entityType]);

  useEffect(() => { load(); }, [load]);

  // Deep-link a fact row to its source chunk in the document viewer. Same
  // in-app navigation contract as SourcesPanel (keep the viewer in-tab).
  function srcLink(row, label) {
    const href = buildEvidenceUrl(
      { document_id: row.document_id, chunk_id: row.source_chunk_id, filename: row.filename, snippet: row.supporting_quote },
      pathname,
    );
    const text = label || row.filename || 'source';
    if (!href) return <span className="ed-src">{text}</span>;
    return (
      <a
        href={href}
        className="citation-link ed-src"
        onClick={(e) => {
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
          e.preventDefault();
          onClose();
          navigate(href);
        }}
      >📄 {text}</a>
    );
  }

  const ent = data?.entity;
  const header = ent?.canonical_name || name || 'Entity';

  return createPortal((
    <div className="np-export-overlay" onClick={onClose}>
      <div
        className="np-export-modal ed-modal"
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: 640, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}
      >
        <div className="np-export-head">
          <div>
            <h3 style={{ margin: 0 }}>{header}</h3>
            {ent && (
              <div className="ed-subtitle">
                {typeLabel(ent.entity_type)}
                {ent.document_count ? ` · appears in ${ent.document_count} document${ent.document_count === 1 ? '' : 's'}` : ''}
                {ent.member_count > 1 ? ` · ${ent.member_count} mentions merged` : ''}
              </div>
            )}
            {ent?.aliases?.length > 0 && (
              <div className="ed-aliases">also: {ent.aliases.join(', ')}</div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {graphExplorerOn && phase === 'ready' && (
              <button
                className="btn btn-sm"
                onClick={() => {
                  // Navigate to the ?graph= deep link (GraphDeepLink owns the
                  // overlay) — browser Back returns here-ish: to the case.
                  onClose();
                  navigate(`${pathname}?graph=e:${ent?.entity_id || entityId}`);
                }}
                title="Browse this entity's connections on the graph canvas"
              >🕸 Graph view</button>
            )}
            <button className="np-icon-btn" onClick={onClose} title="Close">✕</button>
          </div>
        </div>

        <div className="np-export-body ed-body" style={{ display: 'block', padding: 16, overflowY: 'auto' }}>
          {phase === 'loading' && <div className="ed-muted">Loading profile…</div>}
          {phase === 'error' && <div className="bulk-popover-error">{error}</div>}
          {phase === 'empty' && <div className="ed-muted">No graph profile for this entity yet.</div>}
          {phase === 'ready' && (
            <>
              <RoleProfileCards
                caseId={caseId}
                profile={ent.role_profile}
                entityName={ent.canonical_name || name}
                onChanged={() => load({ quiet: true })}
              />
              {ent.description && <p className="ed-desc">{ent.description}</p>}

              <Section title="Relationships" count={data.relationships.length}>
                {data.relationships.map(r => (
                  <div className="ed-row" key={r.id}>
                    <span className="ed-line">
                      <b>{header}</b> {relVerb(r)} <b>{r.neighbor?.canonical_name || '—'}</b>
                    </span>
                    {r.supporting_quote && <span className="ed-quote">“{r.supporting_quote}”</span>}
                    {srcLink(r, r.filename)}
                  </div>
                ))}
              </Section>

              <Section title="Events" count={data.events.length}>
                {data.events.map(ev => (
                  <div className="ed-row" key={ev.event_id}>
                    <span className="ed-line">
                      {ev.occurred_at && <span className="ed-date">{ev.occurred_at}</span>}
                      <b> {typeLabel(ev.event_type) || ev.event_type}</b>
                      {ev.description ? ` — ${ev.description}` : ''}
                    </span>
                    {srcLink({ document_id: ev.document_id, filename: ev.filename }, ev.filename)}
                  </div>
                ))}
              </Section>

              <Section title="Statements made" count={data.claims_made.length}>
                {data.claims_made.map(c => (
                  <div className="ed-row" key={c.claim_id}>
                    <span className="ed-line">{c.claim_text}</span>
                    {c.supporting_quote && <span className="ed-quote">“{c.supporting_quote}”</span>}
                    {srcLink(c, c.filename)}
                  </div>
                ))}
              </Section>

              <Section title="Statements about them" count={data.claims_about.length}>
                {data.claims_about.map(c => (
                  <div className="ed-row" key={c.claim_id}>
                    <span className="ed-line">
                      <b>{c.asserter?.canonical_name || 'Someone'}:</b> {c.claim_text}
                    </span>
                    {c.supporting_quote && <span className="ed-quote">“{c.supporting_quote}”</span>}
                    {srcLink(c, c.filename)}
                  </div>
                ))}
              </Section>

              <Section title="Documents" count={data.documents.length}>
                <div className="ed-doc-chips">
                  {data.documents.map(d => (
                    <span key={d.document_id}>{srcLink({ document_id: d.document_id, filename: d.filename }, d.filename)}</span>
                  ))}
                </div>
              </Section>
            </>
          )}
        </div>
      </div>
    </div>
  ), document.body);
}

function Section({ title, count, children }) {
  if (!count) return null;
  return (
    <div className="ed-section">
      <div className="ed-section-head">{title} <span className="ed-count">{count}</span></div>
      {children}
    </div>
  );
}
